import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@indipia/shared';
import { Button, FilterChip } from '@indipia/ui-core';
import { WORK_TYPE_COLORS } from '../views/scheduleUtils';

type RecipientType = 'CUSTOMER' | 'PARTNER' | 'VENDOR';

interface ShareTask {
  taskId: number;
  workTypeCode: string;
  workTypeName: string;
  startDate: string;
  endDate: string;
  duration: number;
  displayOrder: number;
  assigneePartnerId: number | null;
  foremanName: string;
  foremanContact: string;
}

interface ShareMilestone {
  milestoneId: number;
  milestoneName: string;
  milestoneDate: string;
  dDay: number;
}

interface ShareOrder {
  taskId: number;
  workTypeCode: string;
  workTypeName: string;
  taskStartDate: string;
  taskEndDate: string;
  orderDate: string | null;
  deliveryDate: string | null;
  vendorId: number | null;
  vendorName: string | null;
}

interface ShareViewData {
  recipientType: RecipientType;
  scheduleId: number;
  siteName: string;
  startDate: string;
  endDate: string;
  tasks: ShareTask[];
  dependencies: unknown[];
  nextMilestone: ShareMilestone | null;
  orders?: ShareOrder[];
  // BE(TC_HOLIDAY) 실시간 조회 결과 — GUEST 토큰으론 인증 전용 GET /schedule/holidays를
  // 호출할 수 없어(C-05 §16 #19) getShareView()가 대신 함께 내려준다. ISO 날짜 문자열 배열.
  holidays?: string[];
}

function formatDate(d: string): string {
  const [, m, day] = d.split('-');
  return `${Number(m)}.${Number(day)}`;
}

const DOW_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const GANTT_NAME_W = 112;
const GANTT_LABEL_W = GANTT_NAME_W;
const GANTT_ROW_H = 36;
// 공유 뷰어는 전체 기간 고정(범위 선택 UI 없음) — 날짜 컬럼 최소 폭
const GANTT_DAY_W_MIN = 30;
// 스케줄 관리 스케줄(간트) 화면과 동일한 공종별 색상 팔레트
const GANTT_COLORS = WORK_TYPE_COLORS;

function parseDay(d: string): Date {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

// 열람 링크 오류 4종 + 접속 시간 초과 — 서버 code(SHARE_LINK_*)·data.reason 우선, 없으면 HTTP 상태로 판정.
// 문구는 확정결정 41 / T2 To-Be 화면 B-10~B-13 정의. {회사명}은 응답에 없어 '보낸 곳'으로 안내한다.
type ShareErrorKind = 'EXPIRED' | 'REVOKED' | 'INVALID' | 'TEMPORARY' | 'SESSION';

const SHARE_ERROR_COPY: Record<ShareErrorKind, { title: string; desc: string }> = {
  EXPIRED: { title: '만료된 링크예요', desc: '공유 기간이 끝나 더 이상 열 수 없어요. 새 링크가 필요하면 보낸 곳에 문의해 주세요.' },
  REVOKED: { title: '공유가 취소된 링크예요', desc: '보낸 분이 공유를 취소했어요. 새 링크가 필요하면 보낸 곳에 문의해 주세요.' },
  INVALID: { title: '열 수 없는 링크예요', desc: '링크가 올바르지 않아요. 받으신 알림톡의 버튼을 다시 눌러 보시고, 계속 열리지 않으면 보낸 곳에 문의해 주세요.' },
  TEMPORARY: { title: '연결이 원활하지 않아요', desc: '네트워크를 확인하고 잠시 후 다시 시도해 주세요. 문제가 계속되면 보낸 곳에 문의해 주세요.' },
  SESSION: { title: '접속 시간이 지났어요', desc: '카톡의 버튼을 다시 눌러 열어 주세요.' },
};

function classifyShareError(err: unknown): ShareErrorKind {
  const r = (err as { response?: { status?: number; data?: { code?: string; data?: { reason?: string } } } })?.response;
  const code = r?.data?.code;
  const reason = r?.data?.data?.reason;
  if (code === 'SHARE_LINK_EXPIRED' || reason === 'EXPIRED') return 'EXPIRED';
  if (code === 'SHARE_LINK_REVOKED' || reason === 'REVOKED') return 'REVOKED';
  if (code === 'SHARE_LINK_INVALID' || reason === 'INVALID') return 'INVALID';
  if (code === 'SHARE_LINK_TEMPORARY' || reason === 'TEMPORARY') return 'TEMPORARY';
  const status = r?.status;
  if (status === 410) return 'EXPIRED';
  if (status === 404) return 'INVALID';
  if (status === 401 || status === 403) return 'SESSION';
  return 'TEMPORARY'; // 네트워크 오류·5xx
}

export function ScheduleShareViewPage() {
  const [data, setData] = useState<ShareViewData | null>(null);
  const [errorKind, setErrorKind] = useState<ShareErrorKind | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // 시공반장(PARTNER) 수신자 — 반장보기(담당 배정 이름·연락처)·발주보기(발주일 마커).
  // 거래처(VENDOR) 수신자 — 입고보기(입고일 마커). 반장 이름/연락처는 공유 발송 시점 스냅샷을
  // 백엔드가 미리 조회해 내려주므로(ShareHistoryService#buildPartnerSnapshot) GUEST 열람 시점엔
  // 추가 조회 없이 바로 tasks[].foremanName/foremanContact 로 채워져 있다.
  const [showForeman, setShowForeman] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [showDelivery, setShowDelivery] = useState(false);

  // 컨테이너 실측 너비 — 날짜 컬럼 폭을 컨테이너에 꽉 채우기 위해 동적으로 계산
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);

  useEffect(() => {
    setErrorKind(null);
    apiClient
      .get<{ data: ShareViewData }>('/schedule/schedule-shares/me')
      .then((res) => setData(res.data.data))
      .catch((err) => setErrorKind(classifyShareError(err)));
  }, [reloadKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setContainerW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  if (errorKind) {
    const copy = SHARE_ERROR_COPY[errorKind];
    return (
      <div className="flex h-full min-h-screen flex-col items-center justify-center gap-2 p-8 text-center">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="mb-1 text-text-muted" aria-hidden="true">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <h1 className="text-base font-bold text-text-primary">{copy.title}</h1>
        <p className="text-sm text-text-secondary">{copy.desc}</p>
        {errorKind === 'TEMPORARY' && (
          <Button variant="secondary" size="sm" onClick={() => setReloadKey((k) => k + 1)} className="mt-2">
            다시 시도
          </Button>
        )}
      </div>
    );
  }

  if (!data) {
    return (
      <div aria-busy="true" className="flex h-full min-h-screen items-center justify-center text-sm text-text-muted">
        불러오는 중…
      </div>
    );
  }

  const sortedTasks = [...data.tasks].sort((a, b) => a.displayOrder - b.displayOrder || a.startDate.localeCompare(b.startDate));
  // 동일 공종(workTypeCode)은 스케줄 관리 간트차트처럼 같은 색으로 표시 — 배열 인덱스가 아니라
  // workTypeCode 첫 등장 순서로 색을 배정한다.
  const workTypeColorMap = new Map<string, string>();
  sortedTasks.forEach(t => {
    if (!workTypeColorMap.has(t.workTypeCode)) {
      workTypeColorMap.set(t.workTypeCode, GANTT_COLORS[workTypeColorMap.size % GANTT_COLORS.length]);
    }
  });
  const orderMap = new Map((data.orders ?? []).map(o => [o.taskId, o]));
  // 동일 공종(workTypeCode)은 스케줄 관리 간트차트와 동일하게 한 행에 바를 여러 개 이어서 표시한다.
  // 첫 등장 순서(=sortedTasks 순서, displayOrder 기준)를 그대로 행 순서로 쓴다.
  const rows: { workTypeCode: string; workTypeName: string; bars: ShareTask[] }[] = [];
  const rowIndexByCode = new Map<string, number>();
  sortedTasks.forEach((t) => {
    const idx = rowIndexByCode.get(t.workTypeCode);
    if (idx === undefined) {
      rowIndexByCode.set(t.workTypeCode, rows.length);
      rows.push({ workTypeCode: t.workTypeCode, workTypeName: t.workTypeName, bars: [t] });
    } else {
      rows[idx].bars.push(t);
    }
  });
  const isPartner = data.recipientType === 'PARTNER';
  const isVendor = data.recipientType === 'VENDOR';

  const today = parseDay(new Date().toISOString());

  // 범위는 항상 전체 기간 고정(범위 선택 UI 없음) — 공사기간(스케줄 startDate~endDate) 밖에도
  // 공종 일정이 있으면 스케줄 관리 간트차트처럼 그 범위까지 확장한다. ISO 날짜 문자열은
  // 사전식 정렬이 곧 시간순 정렬이라 min/max 계산에 그대로 사용 가능.
  const taskDates = sortedTasks.flatMap((t) => [t.startDate, t.endDate]);
  const allDates = [data.startDate, data.endDate, ...taskDates].filter(Boolean).sort();
  const ganttStart = parseDay(allDates[0]);
  const ganttEnd = parseDay(allDates[allDates.length - 1]);
  const totalDays = Math.max(daysBetween(ganttStart, ganttEnd) + 1, 1);
  // 컨테이너 너비에 맞춰 날짜 컬럼 폭을 채우되, 가독성을 위한 최소 폭은 보장
  const fitDW = containerW > 200 ? (containerW - GANTT_LABEL_W) / totalDays : GANTT_DAY_W_MIN;
  const GANTT_DAY_W = Math.max(GANTT_DAY_W_MIN, fitDW);
  const todayOffset = today >= ganttStart && today <= ganttEnd ? daysBetween(ganttStart, today) : null;
  const holidaySet = new Set(data.holidays ?? []);
  const ganttDays = Array.from({ length: totalDays }, (_, i) => {
    const d = addDays(ganttStart, i);
    return { date: d, dow: d.getDay(), holiday: holidaySet.has(toISO(d)) };
  });

  const dDiff = daysBetween(today, parseDay(data.endDate));
  const dDayLabel = dDiff > 0 ? `D-${dDiff}` : dDiff === 0 ? 'D-Day' : `D+${Math.abs(dDiff)}`;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-surface">
      {/* 정보바 — 스케줄 관리 스케줄 화면과 동일 (편집 액션 제외) */}
      <div className="flex flex-none items-center gap-3" style={{ padding: '12px 16px', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gt-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>
        {data.siteName && (
          <span className="truncate text-[13px] font-bold text-text-primary">{data.siteName}</span>
        )}
        <span className="text-[length:var(--type-caption-size)] text-text-secondary">{formatDate(data.startDate)} ~ {formatDate(data.endDate)}</span>
        <span style={{ background: 'var(--gt-subtle)', border: 'var(--border-width-hairline) solid var(--shell-border)', color: 'var(--gt-primary)', fontSize: 'var(--type-caption-size)', fontWeight: 700, padding: '3px 10px', borderRadius: 24 }}>
          {dDayLabel}
        </span>
      </div>

      {/* 옵션바 — 시공반장(PARTNER)은 반장보기·발주보기, 거래처(VENDOR)는 입고보기 */}
      {(isPartner || isVendor) && (
        <div className="flex flex-none flex-wrap items-center gap-2" style={{ background: 'var(--shell-bg)', padding: '6px 16px', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}>
          {[
            ...(isPartner ? [
              { label: '반장보기', val: showForeman, set: setShowForeman },
              { label: '발주보기', val: showOrder, set: setShowOrder },
            ] : []),
            ...(isVendor ? [{ label: '입고보기', val: showDelivery, set: setShowDelivery }] : []),
          ].map(({ label, val, set }) => (
            <FilterChip key={label} active={val} onClick={() => set(v => !v)}>
              {label}
            </FilterChip>
          ))}
        </div>
      )}

      {/* 간트 그리드 — 전체 화면 */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        {sortedTasks.length === 0 ? (
          <p className="p-5 text-xs text-text-muted">등록된 공정이 없습니다.</p>
        ) : (
          <div style={{ width: GANTT_LABEL_W + totalDays * GANTT_DAY_W, minWidth: '100%', background: 'var(--shell-surface)' }}>
            {/* 날짜 헤더 */}
            <div className="flex" style={{ background: 'var(--shell-bg)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', position: 'sticky', top: 0, zIndex: 3 }}>
              <div className="sticky left-0 z-10 flex flex-none" style={{ background: 'var(--shell-bg)' }}>
                <div className="flex flex-none items-center text-[10.5px] font-bold text-text-secondary" style={{ width: GANTT_NAME_W, padding: '8px 10px', borderRight: 'var(--border-width-hairline) solid var(--shell-border)' }}>공종</div>
              </div>
              {ganttDays.map((d, i) => (
                <div
                  key={i}
                  className="flex flex-none flex-col items-center gap-0.5 text-center text-[9.5px] leading-tight"
                  style={{
                    width: GANTT_DAY_W,
                    padding: '5px 0 3px',
                    borderRight: 'var(--border-width-hairline) solid var(--shell-border)',
                    color: i === todayOffset ? 'var(--gt-primary)' : (d.dow === 0 || d.holiday) ? 'var(--status-danger-600)' : d.dow === 6 ? 'var(--status-info-600)' : 'var(--shell-on-surface-2)',
                    background: i === todayOffset ? 'var(--gt-subtle)' : (d.dow === 0 || d.dow === 6 || d.holiday) ? 'var(--alpha-black-50)' : 'transparent',
                    fontWeight: i === todayOffset ? 700 : 400,
                  }}
                >
                  <span>{d.date.getMonth() + 1}/{d.date.getDate()}</span>
                  <span style={{ fontSize: 8 }}>{DOW_LABELS[d.dow]}</span>
                </div>
              ))}
            </div>

            {/* 공정 막대 행 — 동일 공종은 한 행에 바를 여러 개 이어서 표시 */}
            <div style={{ position: 'relative' }}>
              {rows.map((row) => {
                const color = workTypeColorMap.get(row.workTypeCode) ?? GANTT_COLORS[0];
                const bars = row.bars.map((t) => {
                  const tStart = parseDay(t.startDate);
                  const tEnd = parseDay(t.endDate);
                  const duration = daysBetween(tStart, tEnd) + 1;
                  // 표시 구간 경계에서 막대가 잘리도록 클램핑 — GanttView.tsx의 getTaskSL()과 동일한 로직
                  const rawStart = daysBetween(ganttStart, tStart);
                  const startOff = Math.max(rawStart, 0);
                  const visibleLen = Math.max(0, Math.min(rawStart + duration, totalDays) - startOff);
                  const order = orderMap.get(t.taskId);
                  const orderBaseIdx    = showOrder    && order?.orderDate    ? daysBetween(ganttStart, parseDay(order.orderDate))    : -1;
                  const deliveryBaseIdx = showDelivery && order?.deliveryDate ? daysBetween(ganttStart, parseDay(order.deliveryDate)) : -1;
                  return { t, duration, startOff, visibleLen, orderBaseIdx, deliveryBaseIdx };
                });
                return (
                  <div key={row.workTypeCode} className="flex items-center" style={{ borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', minHeight: GANTT_ROW_H, background: 'var(--shell-surface)' }}>
                    {/* Sticky left: 공종 */}
                    <div className="sticky left-0 z-10 flex flex-none" style={{ background: 'var(--shell-surface)', minHeight: GANTT_ROW_H }}>
                      <div className="flex flex-none items-center gap-1.5" style={{ width: GANTT_NAME_W, padding: '4px 10px', borderRight: 'var(--border-width-hairline) solid var(--shell-border)' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                        <span className="truncate text-[length:var(--type-caption-size)] text-text-secondary">{row.workTypeName}</span>
                      </div>
                    </div>
                    {/* 일자 그리드 + 막대(들) */}
                    <div className="relative flex flex-1" style={{ minHeight: GANTT_ROW_H }}>
                      {ganttDays.map((gd, di) => {
                        const isOrderCell    = bars.some(b => di === b.orderBaseIdx    && b.orderBaseIdx    >= 0 && b.orderBaseIdx    < totalDays);
                        const isDeliveryCell = bars.some(b => di === b.deliveryBaseIdx && b.deliveryBaseIdx >= 0 && b.deliveryBaseIdx < totalDays);
                        return (
                          <div
                            key={di}
                            style={{ width: GANTT_DAY_W, flexShrink: 0, borderRight: 'var(--border-width-hairline) solid var(--shell-border)', minHeight: GANTT_ROW_H, background: di === todayOffset ? 'var(--gt-subtle)' : (gd.dow === 0 || gd.dow === 6 || gd.holiday) ? 'var(--alpha-black-50)' : 'transparent', position: (isOrderCell || isDeliveryCell) ? 'relative' : undefined }}
                          >
                            {isOrderCell && (
                              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <svg width="11" height="11" viewBox="0 0 12 12" style={{ filter: 'drop-shadow(0 1px 2px var(--alpha-black-300))' }}><polygon points="6,0 12,6 6,12 0,6" fill="var(--gt-primary)" /></svg>
                                <span style={{ fontSize: 7.5, fontWeight: 700, color: 'var(--gt-primary)', whiteSpace: 'nowrap', letterSpacing: -0.2, lineHeight: 1 }}>발주</span>
                              </div>
                            )}
                            {isDeliveryCell && (
                              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <div style={{ width: 11, height: 11, borderRadius: '50%', background: 'var(--shell-on-surface)', boxShadow: 'var(--elevation-1)' }} />
                                <span style={{ fontSize: 7.5, fontWeight: 700, color: 'var(--shell-on-surface)', whiteSpace: 'nowrap', letterSpacing: -0.2, lineHeight: 1 }}>입고</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {bars.map(({ t, duration, startOff, visibleLen }) => (
                        <div key={t.taskId}>
                          {visibleLen > 0 && (
                            <div
                              className="absolute flex items-center justify-center rounded-sm"
                              style={{
                                top: Math.floor((GANTT_ROW_H - 20) / 2),
                                left: startOff * GANTT_DAY_W + 1,
                                width: visibleLen * GANTT_DAY_W - 2,
                                height: 20,
                                background: color,
                                boxShadow: 'var(--elevation-2)',
                              }}
                              title={`${formatDate(t.startDate)}~${formatDate(t.endDate)}`}
                            >
                              <span className="text-[10.5px] font-bold text-group-onPrimary">{duration}일</span>
                            </div>
                          )}
                          {showForeman && t.foremanName && visibleLen > 0 && (
                            <span
                              className="absolute truncate text-[10.5px] font-semibold"
                              style={{
                                top: '50%', transform: 'translateY(-50%)',
                                left: (startOff + visibleLen) * GANTT_DAY_W + 6,
                                maxWidth: 120,
                                color: 'var(--shell-on-surface-2)',
                              }}
                            >
                              {t.foremanName}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
